import { isNil } from 'lodash';
import { CSSProperties } from 'react';
import { MBIconV2Name } from '@shared/components/image';
import { AddressField } from '@shared/data/entity/fields/AddressField';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { AuditTagField } from '@shared/data/entity/fields/AuditTagField';
import { AuditTextField } from '@shared/data/entity/fields/AuditTextField';
import { DateField } from '@shared/data/entity/fields/DateField';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { isEntityBatchField } from '@shared/data/entity/fields/EntityBatchField';
import { Field, castField } from '@shared/data/entity/fields/Field';
import { FileField } from '@shared/data/entity/fields/FileField';
import { GeolocationField } from '@shared/data/entity/fields/GeolocationField';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { PersonField } from '@shared/data/entity/fields/PersonField';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { ScheduledAutomationField } from '@shared/data/entity/fields/ScheduledAutomationField';
import { SignatureField } from '@shared/data/entity/fields/SignatureField';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { TimeTrackerField } from '@shared/data/entity/fields/TimeTrackerField';
import { TodoField } from '@shared/data/entity/fields/TodoField';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { DateRangeEditField } from '@shared/scenes/entity/components/fields/DateRangeEditField';
import {
  isAddressComponent,
  isAuditCheckboxComponent,
  isAuditTagComponent,
  isAuditTextComponent,
  isCombinedTagsComponent,
  isDateComponent,
  isDateRangeComponent,
  isEmailComponent,
  isEntityBatchComponent,
  isFileComponent,
  isGeolocationComponent,
  isInputButtonComponent,
  isNumberComponent,
  isPersonComponent,
  isReferencedInComponent,
  isRelatedCardComponent,
  isRelatedCardLookupComponent,
  isScheduledAutomationComponent,
  isSignatureComponent,
  isStaticTextComponent,
  isSubformComponent,
  isTagComponent,
  isTextComponent,
  isTimeTrackerComponent,
  isTodoComponent,
  isUrlComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { FieldRecord, UpdateField } from '../../types';
import { AddressEditField } from './AddressEditField';
import { AuditCheckboxEditField } from './AuditCheckboxEditField';
import { AuditTagEditField } from './AuditTagEditField';
import { AuditTextEditField } from './AuditTextEditField';
import { DateEditField } from './DateEditField';
import { EmailEditField } from './EmailEditField';
import { FileEditField } from './FileEditField';
import { GenericEditJsonField } from './GenericEditJsonField';
import { GeolocationEditField } from './GeolocationEditField';
import { InputButtonEditField } from './InputButtonEditField';
import { NumberEditField } from './NumberEditField';
import { PersonEditField } from './PersonEditField';
import { ReferencedInEditField } from './ReferencedInEditField';
import { RelatedCardLookupEditField } from './RelatedCardLookupEditField';
import { RelatedEntityEditField } from './RelatedEntityEditField';
import { ScheduledAutomationEditFieldNoCodeWrapper } from './ScheduledAutomationEditField';
import { SignatureEditField } from './SignatureEditField';
import { StaticTextEditField } from './StaticTextEditField';
import { SubformEditField } from './Subform/SubformEditField';
import { TagEditField } from './TagEditField';
import { TextEditField } from './TextEditField';
import { TimeTrackerEditField } from './TimeTrackerEditField';
import { TodoEditField } from './TodoEditField';
import { UrlEditField } from './UrlEditField';

interface Props {
  field: Field | null;
  fields: FieldRecord;
  workflowTemplateId: string;
  component: API.AnyComponent;
  updateField: UpdateField;
  error?: string;
  entityId?: string;
  isAuthenticated?: boolean;
  allowClear?: boolean;
  icon?: MBIconV2Name;
  style?: CSSProperties;
}

export function EditField({
  field,
  fields,
  workflowTemplateId,
  component,
  updateField,
  error,
  entityId,
  isAuthenticated = true,
  allowClear = true,
  icon,
  style,
}: Props) {
  if (isEntityBatchComponent(component) || isCombinedTagsComponent(component)) {
    return null;
  }

  if (isAuditTagComponent(component)) {
    return (
      <AuditTagEditField
        component={component}
        field={castField(field, AuditTagField)}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isAuditTextComponent(component)) {
    return (
      <AuditTextEditField
        component={component}
        field={castField(field, AuditTextField)}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isAuditCheckboxComponent(component)) {
    return (
      <AuditCheckboxEditField
        field={castField(field, AuditCheckboxField)}
        component={component}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }
  if (isStaticTextComponent(component)) {
    return <StaticTextEditField component={component} style={style} />;
  }

  if (isAddressComponent(component)) {
    return (
      <AddressEditField
        component={component}
        updateField={updateField}
        field={castField(field, AddressField)}
        error={error}
        style={style}
      />
    );
  }
  if (isDateComponent(component)) {
    return (
      <DateEditField
        component={component}
        updateField={updateField}
        field={castField(field, DateField)}
        workflowTemplateId={workflowTemplateId}
        entityBatchField={
          isEntityBatchField(fields.entityBatch) && component.allowEntityBatchCreation
            ? fields.entityBatch
            : undefined
        }
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isDateRangeComponent(component)) {
    const startDate = fields[component.startDateComponentId];
    const endDate = fields[component.endDateComponentId];
    return (
      <DateRangeEditField
        field={castField(field, DateRangeField)}
        startDateField={castField(startDate, DateField)}
        endDateField={castField(endDate, DateField)}
        component={component}
        updateField={updateField}
        error={error}
        style={style}
      />
    );
  }

  if (isEmailComponent(component)) {
    return (
      <EmailEditField
        component={component}
        updateField={updateField}
        field={castField(field, EmailField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isFileComponent(component)) {
    return (
      <FileEditField
        component={component}
        updateField={updateField}
        field={castField(field, FileField)}
        error={error}
        style={style}
      />
    );
  }
  if (isGeolocationComponent(component)) {
    return (
      <GeolocationEditField
        component={component}
        updateField={updateField}
        field={castField(field, GeolocationField)}
        error={error}
        showUserName={isAuthenticated}
        style={style}
      />
    );
  }
  if (isNumberComponent(component)) {
    return (
      <NumberEditField
        component={component}
        updateField={updateField}
        field={castField(field, NumberField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isPersonComponent(component)) {
    if (!isAuthenticated) {
      return null;
    }
    return (
      <PersonEditField
        component={component}
        updateField={updateField}
        field={castField(field, PersonField)}
        workflowTemplateId={workflowTemplateId}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isRelatedCardComponent(component)) {
    return (
      <RelatedEntityEditField
        component={component}
        updateField={updateField}
        field={castField(field, RelatedEntityField)}
        fields={fields}
        error={error}
        allowCreate={isAuthenticated}
        allowClear={allowClear}
        entityId={entityId}
        style={style}
      />
    );
  }
  if (isSignatureComponent(component)) {
    return (
      <SignatureEditField
        component={component}
        updateField={updateField}
        field={castField(field, SignatureField)}
        error={error}
        style={style}
      />
    );
  }
  if (isSubformComponent(component)) {
    return (
      <SubformEditField
        component={component}
        updateField={updateField}
        field={castField(field, SubformField)}
        workflowTemplateId={workflowTemplateId}
        error={error}
        allowCreate={isAuthenticated}
        style={style}
      />
    );
  }
  if (isTagComponent(component)) {
    return (
      <TagEditField
        component={component}
        updateField={updateField}
        field={castField(field, TagField)}
        error={error}
        workflowTemplateId={workflowTemplateId}
        allowCreate={isAuthenticated && !!component.addAdditionalValues}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isTextComponent(component)) {
    return (
      <TextEditField
        component={component}
        updateField={updateField}
        field={castField(field, TextField)}
        error={error}
        allowClear={allowClear}
        icon={icon}
        style={style}
      />
    );
  }
  if (isTodoComponent(component)) {
    return (
      <TodoEditField
        component={component}
        updateField={updateField}
        field={castField(field, TodoField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isTimeTrackerComponent(component)) {
    return (
      <TimeTrackerEditField
        component={component}
        updateField={updateField}
        field={castField(field, TimeTrackerField)}
        error={error}
        style={style}
      />
    );
  }
  if (isInputButtonComponent(component)) {
    return (
      <InputButtonEditField
        component={component}
        inputField={fields[component.inputComponentId]}
        workflowTemplateId={workflowTemplateId}
        updateField={updateField}
        style={style}
      />
    );
  }
  if (isRelatedCardLookupComponent(component)) {
    return (
      <RelatedCardLookupEditField
        field={field}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        component={component}
        error={error}
        style={style}
      />
    );
  }
  if (isScheduledAutomationComponent(component)) {
    return (
      <ScheduledAutomationEditFieldNoCodeWrapper
        component={component}
        updateField={updateField}
        field={castField(field, ScheduledAutomationField)}
        dateComponentField={fields[component.dateComponentId] as DateField}
        error={error}
        isReadOnly={component.readonly ?? !isAuthenticated}
        workflowTemplateId={workflowTemplateId}
        showLabel
        allowClear={allowClear}
        style={style}
      />
    );
  }
  if (isReferencedInComponent(component)) {
    return !isNil(entityId) ? (
      <ReferencedInEditField component={component} entityId={entityId} style={style} />
    ) : null;
  }
  if (isUrlComponent(component)) {
    return (
      <UrlEditField
        component={component}
        updateField={updateField}
        field={castField(field, UrlField)}
        error={error}
        allowClear={allowClear}
        style={style}
      />
    );
  }

  return <GenericEditJsonField component={component} updateField={updateField} field={field} />;
}
